import React, { useState, useEffect, useRef } from 'react'
import * as api from '../../services/api.js'
import { countWords } from '../../hooks/useWordCount.js'
import { todayShort } from '../../utils/dates.js'
import Card from '../../components/Card.jsx'

function stripHtml(html) {
  return html ? html.replace(/<[^>]+>/g, ' ') : ''
}

const ALLOWED_TAGS = new Set([
  'p','br','b','strong','i','em','u','s','ul','ol','li',
  'h1','h2','h3','blockquote','span','div','mark','hr','details','summary',
])

function sanitiseHtml(html) {
  if (!html) return ''
  return html.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (tag, name) => {
    const n = name.toLowerCase()
    if (!ALLOWED_TAGS.has(n)) return ''
    if (tag.startsWith('</')) return `</${n}>`
    const styleM = tag.match(/\bstyle\s*=\s*"([^"]*)"/i)
    const classM = tag.match(/\bclass\s*=\s*"([^"]*)"/i)
    let out = `<${n}`
    if (styleM) out += ` style="${styleM[1]}"`
    if (classM) out += ` class="${classM[1]}"`
    if (n === 'hr') out += '/>'
    else out += '>'
    return out
  })
}

const HIGHLIGHT_COLOURS = [
  { label: 'Yellow', value: 'hl-yellow', hex: '#fff176' },
  { label: 'Green',  value: 'hl-green',  hex: '#bbf7d0' },
  { label: 'Pink',   value: 'hl-pink',   hex: '#fbcfe8' },
  { label: 'Blue',   value: 'hl-blue',   hex: '#bae6fd' },
  { label: 'Purple', value: 'hl-purple', hex: '#e9d5ff' },
  { label: 'Orange', value: 'hl-orange', hex: '#fed7aa' },
]

const INSERT_OPTIONS = [
  { id: 'h1',        label: 'H1 Heading' },
  { id: 'h2',        label: 'H2 Heading' },
  { id: 'divider',   label: '— Divider' },
  { id: 'callout',   label: '✦ Callout' },
  { id: 'toggle',    label: '▶ Toggle block' },
  { id: 'checklist', label: '☐ Checklist item' },
]

function AlignLeftIcon() {
  return (
    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true">
      <rect x="0" y="0" width="14" height="2" rx="1" fill="currentColor"/>
      <rect x="0" y="5" width="10" height="2" rx="1" fill="currentColor"/>
      <rect x="0" y="10" width="12" height="2" rx="1" fill="currentColor"/>
    </svg>
  )
}

function AlignCenterIcon() {
  return (
    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true">
      <rect x="0" y="0" width="14" height="2" rx="1" fill="currentColor"/>
      <rect x="2" y="5" width="10" height="2" rx="1" fill="currentColor"/>
      <rect x="1" y="10" width="12" height="2" rx="1" fill="currentColor"/>
    </svg>
  )
}

function AlignRightIcon() {
  return (
    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true">
      <rect x="0" y="0" width="14" height="2" rx="1" fill="currentColor"/>
      <rect x="4" y="5" width="10" height="2" rx="1" fill="currentColor"/>
      <rect x="2" y="10" width="12" height="2" rx="1" fill="currentColor"/>
    </svg>
  )
}

export default function Editor({ pid = 'w', onChapChange, onAddNote }) {
  const [chapters, setChapters]   = useState([])
  const [project, setProject]     = useState({})
  const [general, setGeneral]     = useState('')
  const [sessions, setSessions]   = useState([])
  const [chapTexts, setChapTexts] = useState({})
  const [notes, setNotes]         = useState({})

  useEffect(() => {
    api.getChapters(pid).then(d => setChapters(d || []))
    api.getWritingProjectData(pid).then(d => setProject(d || {}))
    api.getWritingText(pid, 'general').then(d => setGeneral(d || ''))
    api.getWritingSessions(pid).then(d => setSessions(d || []))
  }, [pid])

  const [chapId, setChapId]                   = useState('general')
  const [selBar, setSelBar]                   = useState({ visible: false, x: 0, y: 0, text: '' })
  const [showSaved, setShowSaved]             = useState(false)
  const [showNotes, setShowNotes]             = useState(false)
  const [noteText, setNoteText]               = useState('')
  const [milestone, setMilestone]             = useState('')
  const [activeHighlight, setActiveHighlight] = useState('hl-yellow')
  const [showHighlightPicker, setShowHighlightPicker] = useState(false)
  const [showInsert, setShowInsert]           = useState(false)
  const [editorChars, setEditorChars]         = useState(0)
  const shownMilestones                       = useRef(new Set())

  const editorRef    = useRef(null)
  const saveTimer    = useRef(null)
  const loadingRef   = useRef(false)
  const hlPickerRef  = useRef(null)
  const insertRef    = useRef(null)

  // Outside-click close for highlight picker and insert menu
  useEffect(() => {
    function onDown(e) {
      if (hlPickerRef.current && !hlPickerRef.current.contains(e.target)) {
        setShowHighlightPicker(false)
      }
      if (insertRef.current && !insertRef.current.contains(e.target)) {
        setShowInsert(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  // Escape closes dropdowns
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { setShowHighlightPicker(false); setShowInsert(false) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Load content whenever chapter selection changes
  useEffect(() => {
    if (!editorRef.current) return
    if (chapId === 'general') {
      loadingRef.current = true
      editorRef.current.innerHTML = sanitiseHtml(general)
      loadingRef.current = false
      setNoteText(notes['general'] || '')
    } else {
      const key = String(chapId)
      if (chapTexts[key] !== undefined) {
        loadingRef.current = true
        editorRef.current.innerHTML = sanitiseHtml(chapTexts[key])
        loadingRef.current = false
      } else {
        api.getWritingText(pid, key).then(html => {
          setChapTexts(prev => ({ ...prev, [key]: html || '' }))
          loadingRef.current = true
          if (editorRef.current) editorRef.current.innerHTML = sanitiseHtml(html)
          loadingRef.current = false
        })
      }
      if (notes[`note-${key}`] !== undefined) {
        setNoteText(notes[`note-${key}`] || '')
      } else {
        api.getWritingText(pid, `note-${key}`).then(t => {
          const text = t || ''
          setNotes(prev => ({ ...prev, [`note-${key}`]: text }))
          setNoteText(text)
        })
      }
    }
    setTimeout(() => {
      if (editorRef.current) {
        const t = editorRef.current.innerText || ''
        setEditorChars(t.replace(/\s+/g, '').length)
      }
    }, 60)
  }, [chapId])

  useEffect(() => { onChapChange?.(chapId) }, [chapId])

  function handleEditorMouseUp() {
    const sel = window.getSelection()
    if (sel && sel.toString().trim().length > 2) {
      const range = sel.getRangeAt(0)
      const rect  = range.getBoundingClientRect()
      setSelBar({ visible: true, x: rect.left + rect.width / 2, y: rect.top - 44, text: sel.toString() })
    } else {
      setSelBar({ visible: false, x: 0, y: 0, text: '' })
    }
  }

  function dismissSelBar() {
    setSelBar({ visible: false, x: 0, y: 0, text: '' })
  }

  function handleAddNoteFromSelection() {
    const text = selBar.text
    dismissSelBar()
    onAddNote?.(text)
  }

  function currentHtml() {
    return editorRef.current ? editorRef.current.innerHTML : ''
  }

  function saveContent() {
    if (!editorRef.current) return
    const html = currentHtml()
    const key = chapId === 'general' ? 'general' : String(chapId)
    if (chapId === 'general') {
      setGeneral(html)
    } else {
      setChapTexts(prev => ({ ...prev, [key]: html }))
    }
    api.saveWritingText(pid, key, html)
    logSession()
    setShowSaved(true)
    setTimeout(() => setShowSaved(false), 1800)
  }

  function onInput() {
    if (loadingRef.current) return
    const t = editorRef.current?.innerText || ''
    setEditorChars(t.replace(/\s+/g, '').length)
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(saveContent, 1200)
  }

  function logSession() {
    const today   = todayShort()
    const allText = stripHtml(general) + Object.values(chapTexts).map(stripHtml).join(' ')
    const total   = countWords(allText)
    const session = { date: today, words: total }
    setSessions(prev => {
      const next = [session, ...prev.filter(s => s.date !== today)].slice(0, 30)
      return next
    })
    api.saveWritingSession(pid, session.date, session.words)
  }

  function fmt(e, cmd, value = null) {
    e.preventDefault()
    editorRef.current?.focus()
    document.execCommand(cmd, false, value)
  }

  function applyHighlight(e) {
    e.preventDefault()
    editorRef.current?.focus()
    const colour = HIGHLIGHT_COLOURS.find(c => c.value === activeHighlight)
    document.execCommand('hiliteColor', false, colour?.hex || '#fff176')
    setShowHighlightPicker(false)
  }

  function handleClear(e) {
    e.preventDefault()
    editorRef.current?.focus()
    document.execCommand('removeFormat', false, null)
  }

  function insertBlock(type) {
    editorRef.current?.focus()
    switch (type) {
      case 'h1':
        document.execCommand('formatBlock', false, 'h1')
        break
      case 'h2':
        document.execCommand('formatBlock', false, 'h2')
        break
      case 'divider':
        document.execCommand('insertHTML', false, '<hr/><p><br/></p>')
        break
      case 'callout':
        document.execCommand('insertHTML', false, '<div class="editor-callout"><p>✦ Callout text</p></div><p><br/></p>')
        break
      case 'toggle':
        document.execCommand('insertHTML', false, '<details><summary>Click to expand</summary><p>Content here…</p></details><p><br/></p>')
        break
      case 'checklist':
        document.execCommand('insertHTML', false, '<p class="editor-checklist">☐ Checklist item</p>')
        break
    }
    setShowInsert(false)
  }

  function handleFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const text = ev.target.result
      const html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n\n+/g, '</p><p>')
        .replace(/\n/g, '<br>')
      if (editorRef.current) {
        const existing = editorRef.current.innerHTML
        editorRef.current.innerHTML = existing
          ? `${existing}<p>${html}</p>`
          : `<p>${html}</p>`
        saveContent()
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  function saveNote() {
    const noteKey = chapId === 'general' ? 'general' : `note-${chapId}`
    setNotes(prev => ({ ...prev, [noteKey]: noteText }))
    api.saveWritingText(pid, noteKey, noteText)
  }

  const totalWords = countWords(stripHtml(general))
    + Object.values(chapTexts).reduce((s, t) => s + countWords(stripHtml(t)), 0)
    + chapters.reduce((s, c) => s + countWords(c.text || ''), 0)

  const MILESTONES = [
    { words: 1000,  message: '1,000 words — you\'ve started something real.' },
    { words: 5000,  message: '5,000 words — a proper beginning.' },
    { words: 10000, message: '10,000 words — a tenth of a novel. Keep going.' },
    { words: 25000, message: '25,000 words — you\'re a quarter of the way to a full novel.' },
  ]

  useEffect(() => {
    const hit = MILESTONES.filter(m => totalWords >= m.words && !shownMilestones.current.has(m.words))
    if (hit.length) {
      const top = hit[hit.length - 1]
      shownMilestones.current.add(top.words)
      setMilestone(top.message)
      setTimeout(() => setMilestone(''), 5000)
    }
  }, [totalWords])

  const target   = parseInt(project.length || 0)
  const progress = target ? Math.min(100, Math.round((totalWords / target) * 100)) : 0

  const currentChapLabel = chapId === 'general'
    ? 'General / no chapter'
    : chapters.find(c => String(c.id) === String(chapId))?.title || 'Untitled chapter'

  const activeHlColour = HIGHLIGHT_COLOURS.find(c => c.value === activeHighlight)

  return (
    <Card label="Write" title="Your story">
      {/* Chapter selector + file upload */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <label className="w-label" style={{ margin: 0, flexShrink: 0 }}>Chapter:</label>
        <select
          className="w-select"
          style={{ flex: 1, maxWidth: 280 }}
          value={chapId}
          onChange={e => setChapId(e.target.value)}
        >
          <option value="general">General / no chapter</option>
          {chapters.map(c => (
            <option key={c.id} value={c.id}>{c.title || 'Untitled chapter'}</option>
          ))}
        </select>

        <label className="ghost-btn" style={{ cursor: 'pointer', margin: 0, flexShrink: 0 }}>
          ↑ Import .txt / .md
          <input type="file" accept=".txt,.md" onChange={handleFileUpload} style={{ display: 'none' }} />
        </label>
      </div>

      {/* Progress bar toward word target */}
      {target > 0 && (
        <>
          <div className="w-progress-bar-wrap">
            <div className="w-progress-bar" style={{ width: `${progress}%` }} />
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--stone)', marginBottom: '0.75rem' }}>
            {totalWords.toLocaleString()} of {target.toLocaleString()} words ({progress}%)
          </p>
        </>
      )}

      {/* Formatting toolbar */}
      <div className="rich-toolbar">
        {/* Text format */}
        <button className="rich-btn" title="Bold"          onMouseDown={e => fmt(e, 'bold')}><strong>B</strong></button>
        <button className="rich-btn" title="Italic"        onMouseDown={e => fmt(e, 'italic')}><em>I</em></button>
        <button className="rich-btn" title="Underline"     onMouseDown={e => fmt(e, 'underline')}><u>U</u></button>
        <button className="rich-btn" title="Strikethrough" onMouseDown={e => fmt(e, 'strikeThrough')}><s>S</s></button>

        <div className="rich-sep" />

        {/* Highlight with colour picker */}
        <div ref={hlPickerRef} style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            <button
              className="rich-btn toolbar-btn--highlight"
              title="Highlight"
              onMouseDown={applyHighlight}
              style={{ background: activeHlColour?.hex || '#fff176', color: '#555', borderRadius: '6px 0 0 6px', paddingRight: 5 }}
            >
              H
            </button>
            <button
              className="rich-btn"
              title="Choose highlight colour"
              onMouseDown={e => { e.preventDefault(); setShowHighlightPicker(v => !v) }}
              style={{ padding: '4px 5px', borderRadius: '0 6px 6px 0', borderLeft: '1px solid rgba(0,0,0,0.12)' }}
            >
              ▾
            </button>
          </div>
          {showHighlightPicker && (
            <div className="highlight-picker">
              {HIGHLIGHT_COLOURS.map(c => (
                <button
                  key={c.value}
                  className={`highlight-swatch${activeHighlight === c.value ? ' active' : ''}`}
                  title={c.label}
                  onMouseDown={e => { e.preventDefault(); setActiveHighlight(c.value); setShowHighlightPicker(false) }}
                  style={{ background: c.hex }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="rich-sep" />

        {/* Alignment */}
        <button className="rich-btn" title="Align left"   onMouseDown={e => fmt(e, 'justifyLeft')}><AlignLeftIcon /></button>
        <button className="rich-btn" title="Align centre" onMouseDown={e => fmt(e, 'justifyCenter')}><AlignCenterIcon /></button>
        <button className="rich-btn" title="Align right"  onMouseDown={e => fmt(e, 'justifyRight')}><AlignRightIcon /></button>

        <div className="rich-sep" />

        {/* Insert dropdown */}
        <div ref={insertRef} style={{ position: 'relative' }}>
          <button
            className="rich-btn"
            title="Insert block"
            onMouseDown={e => { e.preventDefault(); setShowInsert(v => !v) }}
            style={{ fontSize: '0.72rem' }}
          >
            + Insert ▾
          </button>
          {showInsert && (
            <div className="insert-menu">
              {INSERT_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  className="insert-menu__option"
                  onMouseDown={e => { e.preventDefault(); insertBlock(opt.id) }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rich-sep" />

        <button className="rich-btn" title="Clear formatting" onMouseDown={handleClear} style={{ fontSize: '0.72rem' }}>
          ✕ Clear
        </button>
      </div>

      {/* Contenteditable rich editor */}
      <div
        ref={editorRef}
        className="w-rich-editor"
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Begin here. The first sentence doesn't have to be good — it just has to exist..."
        onInput={onInput}
        onMouseUp={handleEditorMouseUp}
        onBlur={e => { if (!e.relatedTarget?.closest?.('.selection-toolbar')) dismissSelBar() }}
      />

      {/* Floating selection toolbar */}
      {selBar.visible && (
        <div
          className="selection-toolbar"
          style={{ left: selBar.x, top: selBar.y }}
          onMouseDown={e => e.preventDefault()}
        >
          <button onClick={handleAddNoteFromSelection}>💬 Add note</button>
          <button onClick={() => { fmt({ preventDefault: () => {} }, 'hiliteColor', activeHlColour?.hex || '#fff176'); dismissSelBar() }}>
            🖊 Highlight
          </button>
        </div>
      )}

      {/* Milestone celebration */}
      {milestone && (
        <div style={{ fontSize: '0.78rem', color: 'var(--accent)', fontStyle: 'italic', marginTop: '0.5rem', animation: 'fadeIn 0.3s ease' }}>
          {milestone}
        </div>
      )}

      {/* Status bar */}
      <div className="w-editor-bar" style={{ marginTop: '0.6rem' }}>
        <span className="w-word-count">{totalWords.toLocaleString()} word{totalWords !== 1 ? 's' : ''} total</span>
        <span className="editor-counts">{editorChars.toLocaleString()} char{editorChars !== 1 ? 's' : ''}</span>
        {showSaved && (
          <span style={{ fontSize: '0.75rem', color: 'var(--sage)', fontStyle: 'italic' }}>Saved ✓</span>
        )}
        <button className="ghost-btn" onClick={() => setShowNotes(v => !v)}>
          {showNotes ? 'Hide notes' : 'Notes'}
        </button>
      </div>

      {/* Notes panel */}
      {showNotes && (
        <div className="w-notes-wrap">
          <p style={{ fontSize: '0.7rem', fontWeight: 500, color: 'var(--stone)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
            Notes — {currentChapLabel}
          </p>
          <textarea
            className="w-textarea"
            rows={4}
            placeholder="Write a note about this passage..."
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            onBlur={saveNote}
          />
          <button className="ghost-btn" onClick={saveNote} style={{ marginTop: '0.5rem' }}>Save note</button>
        </div>
      )}
    </Card>
  )
}
